<template>
  <data-table-wrapper
    :filter-config="filterConfig"
    :default-sort="{ by: 'startDate', desc: true }"
    :default-items-per-page="15"
    :enable-search="true"
    :search-config="{
      urlKey: 'search',
      placeholder: 'Suchen...',
      debounce: 100,
    }"
    :footer-props="{ 'items-per-page-options': [15, 25, 50] }"
    @filters-changed="onFiltersChanged"
  >
    <template
      #default="{
        tableProps,
        tableEvents,
        filters,
        updateFilter,
        searchValue,
        updateSearch,
      }"
    >
      <div class="reports-list-wrapper">
        <!-- Fixed Header -->
        <v-card flat class="reports-list-header">
          <!-- Row 1: Search and Sort -->
          <v-toolbar flat dense class="rounded-0">
            <v-text-field
              :value="searchValue"
              @input="updateSearch"
              dense
              outlined
              hide-details="auto"
              placeholder="Suchen"
              prepend-inner-icon="mdi-magnify"
              clearable
              style="max-width: 200px"
              class="mr-2"
            ></v-text-field>

            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn small depressed v-bind="attrs" v-on="on">
                  <v-icon small left>mdi-sort</v-icon>
                  {{ getSortLabel(tableProps.sortBy, tableProps.sortDesc) }}
                  <v-icon small right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense nav>
                <v-list-item
                  v-for="option in sortOptions"
                  :key="option.value"
                  @click="updateSort(option.value, option.desc, tableEvents)"
                  :class="{
                    'v-list-item--active': tableProps.sortBy[0] === option.value && tableProps.sortDesc[0] === option.desc,
                  }"
                >
                  <v-list-item-icon class="mr-3">
                    <v-icon small>{{ option.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ option.text }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action v-if="tableProps.sortBy[0] === option.value && tableProps.sortDesc[0] === option.desc">
                    <v-icon small color="primary">mdi-check</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn
              small
              depressed
              class=" mx-1"
              @click="expandAllGroups"
              :disabled="!hasClosedGroups"
            >
              <v-icon  small >mdi-unfold-more-horizontal</v-icon>
              
            </v-btn>
            <v-btn
              small
              depressed
              @click="collapseAllGroups"
              :disabled="!hasOpenGroups"
            >
              <v-icon small >mdi-unfold-less-horizontal</v-icon>
            </v-btn>
          </v-toolbar>
          <v-divider></v-divider>

          <!-- Row 2: Filters -->
          <v-toolbar flat dense class="rounded-0">
            

            <!-- Status Filter Menu -->
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn small depressed v-bind="attrs" v-on="on" class="mr-1">
                  <v-icon small left>mdi-filter-variant</v-icon>
                  {{ getStatusFilterLabel(filters.status) }}
                  <v-icon small right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense nav>
                <v-list-item
                  v-for="option in statusOptions"
                  :key="option.value"
                  @click="updateFilter('status', option.value)"
                  :class="{
                    'v-list-item--active': filters.status === option.value,
                  }"
                >
                  <v-list-item-icon v-if="option.icon" class="mr-3">
                    <v-icon small>{{ option.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ option.text }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action v-if="filters.status === option.value">
                    <v-icon small color="primary">mdi-check</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Assigned To Filter Menu -->
            <v-menu v-if="showAllReports" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn small depressed v-bind="attrs" v-on="on" class="mr-1">
                  <v-icon small left>mdi-account</v-icon>
                  {{ getAssignedToFilterLabel(filters.assignedTo) }}
                  <v-icon small right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense nav>
                <v-list-item
                  v-for="option in assignedToOptions"
                  :key="option.value"
                  @click="updateFilter('assignedTo', option.value)"
                  :class="{
                    'v-list-item--active': filters.assignedTo === option.value,
                  }"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ option.text }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action
                    v-if="filters.assignedTo === option.value"
                  >
                    <v-icon small color="primary">mdi-check</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Type Filter Menu -->
            <v-menu offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-btn small depressed v-bind="attrs" v-on="on" class="mr-1">
                  <v-icon small left>mdi-tag</v-icon>
                  {{ getTypeFilterLabel(filters.type) }}
                  <v-icon small right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense nav>
                <v-list-item @click="clearTypeFilter(updateFilter)">
                  <v-list-item-action class="mr-3">
                    <v-icon small :color="isAllTypesSelected(filters.type) ? 'primary' : ''">
                      {{ isAllTypesSelected(filters.type) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Alle Dienstarten</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="my-1"></v-divider>
                <v-list-item
                  v-for="option in typeOptionsWithoutAll"
                  :key="option.value"
                  @click="toggleTypeFilter(option.value, filters.type, updateFilter)"
                >
                  <v-list-item-action class="mr-3">
                    <v-icon small :color="isTypeSelected(option.value, filters.type) ? 'primary' : ''">
                      {{ isTypeSelected(option.value, filters.type) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ option.text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-toolbar>
          <v-divider></v-divider>
        </v-card>

        <!-- Data Table -->
        <v-card flat class="reports-list-content">
          <!-- Loading State -->
          <template v-if="loading">
            <v-list>
              <v-list-item v-for="i in 5" :key="i">
                <v-list-item-content>
                  <v-skeleton-loader
                    type="list-item-two-line"
                  ></v-skeleton-loader>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>

          <!-- Loaded Content -->
          <v-data-table
            v-else
            v-bind="tableProps"
            v-on="tableEvents"
            :headers="headers"
            :items="filteredReports"
            :search="searchValue"
            :custom-filter="customFilter"
            group-by="monthGroup"
            hide-default-header
            :footer-props="footerProps"
            dense
            class="compact-table"
          >
            <!-- Group Header -->
            <template v-slot:group.header="{ group, headers, toggle, isOpen }">
              <td :colspan="headers.length" class="pa-0 rounded-0 ">
                <th :colspan="headers.length" >
              <v-btn
                @click="() => { toggle(); updateGroupState(); }"
                small
                icon
                class="mr-1"
                :ref="group"
                :data-open="isOpen"
              >
                <v-icon>
                  {{ isOpen ? "mdi-chevron-down" : "mdi-chevron-right" }}
                </v-icon>
              </v-btn>
                {{ formatGroupLabel(group) }}
              <span class="font-weight-regular grey--text">
                ({{ getCountOfReportsInMonth(group) }})
              </span>
            </th>
              </td>
            </template>

            <!-- Item Row -->
            <template v-slot:item="{ item }">
              <tr
                :class="{ 'selected-item': item.meta.id === selectedId }"
                @click="selectReport(item)"
                style="cursor: pointer"
              >
                <td class="pa-0">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        <div>{{ item.title || "Ohne Titel" }}</div>
                      </v-list-item-title>
                      <v-list-item-subtitle class="mt-1">
                        <report-status-chip
                          :status="item.status"
                          small
                          class="mr-1"
                        />
                        <DutyTypeChip
                          v-if="item.type"
                          :dutyType="item.type"
                          small
                          class="mr-1"
                        />
                      </v-list-item-subtitle>
                      <v-list-item-subtitle class="mt-1">
                        <div style="line-height: 1.6">
                          <div>
                            <v-icon small left>mdi-clock-outline</v-icon>
                            <span
                              :class="
                                !item.duration?.start?.timestamp
                                  ? 'grey--text'
                                  : ''
                              "
                            >
                              {{ formatTimeRange(item.duration) }}
                            </span>
                          </div>
                          <div>
                            <v-icon small left>mdi-account</v-icon>
                            {{
                              item.assignedTo?.displayName || "Nicht zugewiesen"
                            }}
                          </div>
                          <div v-if="item.correctedBy?.displayName">
                            <v-icon small left>mdi-account-edit</v-icon>
                            {{ item.correctedBy.displayName }}
                          </div>
                        </div>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    
                  </v-list-item>
                </td>
              </tr>
            </template>

            <!-- No Data -->
            <template v-slot:no-data>
              <div class="pa-8 text-center">
                <v-icon size="64" color="grey lighten-1"
                  >mdi-file-document-outline</v-icon
                >
                <div class="mt-4 text-body-1 grey--text">
                  Keine Berichte gefunden
                </div>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </div>
    </template>
  </data-table-wrapper>
</template>

<script>
import { auth } from "@/firebase";
import ReportStatusChip from "@/components/duty-management/ReportStatusChip.vue";
import DutyTypeChip from "@/components/duty-management/DutyTypeChip.vue";
import DataTableWrapper from "@/components/_system/DataTableWrapper.vue";
import { DUTY_MANAGEMENT_REPORTS_read_all } from "@/data/permission-types.js";

export default {
  name: "ReportsList",
  components: {
    ReportStatusChip,
    DutyTypeChip,
    DataTableWrapper,
  },
  props: {
    reports: {
      type: Array,
      required: true,
    },
    selectedId: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentUserUid: auth.currentUser.uid,
      currentFilters: {}, // Store current filters from DataTableWrapper
      allGroupsExpanded: true,
      hasClosedGroups: false,
      hasOpenGroups: true,
      statusOptions: [
        { value: "all", text: "Alle", icon: "mdi-format-list-bulleted" },
        { value: "inProgress", text: "In Bearbeitung", icon: "mdi-pencil" },
        { value: "toBeReviewed", text: "Zu prüfen", icon: "mdi-check-all" },
        {
          value: "inCorrection",
          text: "In Korrektur",
          icon: "mdi-pencil-box-multiple",
        },
      ],
      assignedToOptions: [
        { value: "all", text: "Alle Berichte" },
        { value: "own", text: "Eigene Berichte" },
      ],
      sortOptions: [
        { value: "startDate", desc: true, text: "Neueste zuerst", icon: "mdi-sort-calendar-descending" },
        { value: "startDate", desc: false, text: "Älteste zuerst", icon: "mdi-sort-calendar-ascending" },
      ],
      footerProps: {
        "items-per-page-options": [25, 50, 100, 150, 200, -1],
      },
    };
  },
  computed: {
    showAllReports() {
      return this.$store.getters["organization/checkPermissionByID"](
        `${DUTY_MANAGEMENT_REPORTS_read_all}`
      );
    },

    /**
     * Type options derived from available reports
     */
    typeOptions() {
      const types = new Map();
      this.reports.forEach((report) => {
        if (report.type?.id && report.type?.title) {
          types.set(report.type.id, report.type.title);
        }
      });
      
      const options = [{ value: "all", text: "Alle Dienstarten" }];
      types.forEach((title, id) => {
        options.push({ value: id, text: title });
      });
      
      return options;
    },

    /**
     * Type options without the "all" option for multi-select
     */
    typeOptionsWithoutAll() {
      return this.typeOptions.filter(opt => opt.value !== "all");
    },

    /**
     * Filter configuration for DataTableWrapper
     */
    filterConfig() {
      return {
        status: { default: "all", urlKey: "status" },
        assignedTo: { default: "own", urlKey: "assignedTo" },
        type: { default: [], urlKey: "type" },
      };
    },

    /**
     * Headers for v-data-table (hidden but needed)
     */
    headers() {
      return [
        {
          text: "Bericht",
          value: "title",
          sortable: false,
        },
      ];
    },

    /**
     * Filtered reports based on current filters
     */
    filteredReports() {
      let filtered = [...this.reports];

      // Apply status filter
      if (this.currentFilters.status && this.currentFilters.status !== "all") {
        filtered = filtered.filter(
          (report) => report.status === this.currentFilters.status
        );
      }

      // Apply assigned to filter
      if (
        this.currentFilters.assignedTo &&
        this.currentFilters.assignedTo !== "all"
      ) {
        if (this.currentFilters.assignedTo === "own") {
          filtered = filtered.filter(
            (report) => report.assignedTo?.uid === this.currentUserUid
          );
        }
      }

      // Apply type filter (multi-select)
      const typeFilter = this.currentFilters.type;
      if (typeFilter && typeFilter !== "none") {
        const typeArray = Array.isArray(typeFilter) ? typeFilter : [typeFilter];
        if (typeArray.length > 0) {
          filtered = filtered.filter(
            (report) => typeArray.includes(report.type?.id)
          );
        }
      }

      // Always add month group for grouping
      filtered = filtered.map((report) => {
        const date = report.duration?.start?.timestamp?.toDate?.();
        let monthGroup = "Kein Datum";

        if (date) {
          const month = ("0" + (date.getMonth() + 1)).slice(-2);
          const year = date.getFullYear();
          const monthLong = date.toLocaleString("de-DE", { month: "long" });
          // Format: "2026-01 - Januar 2026" for proper sorting
          monthGroup = `${year}-${month} - ${monthLong} ${year}`;
        }

        return {
          ...report,
          monthGroup,
        };
      });

      return filtered;
    },
  },
  methods: {
    onFiltersChanged(filters) {
      this.currentFilters = filters;
    },

    /**
     * Custom filter function for search
     */
    customFilter(value, search, item) {
      if (!search) return true;
      const searchLower = search.toLowerCase();

      return (
        item.title?.toLowerCase().includes(searchLower) ||
        item.assignedTo?.displayName?.toLowerCase().includes(searchLower) ||
        item.type?.name?.toLowerCase().includes(searchLower) ||
        item.status?.toLowerCase().includes(searchLower)
      );
    },

    /**
     * Get label for status filter
     */
    getStatusFilterLabel(status) {
      const option = this.statusOptions.find((opt) => opt.value === status);
      return option ? option.text : "Status";
    },

    /**
     * Get label for assigned to filter
     */
    getAssignedToFilterLabel(assignedTo) {
      const option = this.assignedToOptions.find(
        (opt) => opt.value === assignedTo
      );
      return option ? option.text : "Zugewiesen";
    },

    /**
     * Get label for type filter
     */
    getTypeFilterLabel(type) {
      if (!type || !Array.isArray(type) || type.length === 0) {
        return "Alle Dienstarten";
      }
      if (type.length === 1) {
        const option = this.typeOptions.find((opt) => opt.value === type[0]);
        return option ? option.text : "1 Dienstart";
      }
      return `${type.length} Dienstarten`;
    },

    /**
     * Check if a type is selected
     */
    isTypeSelected(typeValue, currentTypes) {
      if (!currentTypes) return false;
      const types = Array.isArray(currentTypes) ? currentTypes : [currentTypes];
      return types.includes(typeValue);
    },

    /**
     * Check if "All types" is selected (empty array or no filter)
     */
    isAllTypesSelected(currentTypes) {
      if (!currentTypes) return true;
      if (Array.isArray(currentTypes)) return currentTypes.length === 0;
      return false;
    },

    /**
     * Toggle a type in the filter
     */
    toggleTypeFilter(typeValue, currentTypes, updateFilter) {
      const types = Array.isArray(currentTypes) ? [...currentTypes] : [];
      const index = types.indexOf(typeValue);
      if (index > -1) {
        types.splice(index, 1);
      } else {
        types.push(typeValue);
      }
      updateFilter('type', types);
    },

    /**
     * Clear type filter (select all)
     */
    clearTypeFilter(updateFilter) {
      updateFilter('type', []);
    },

    selectReport(report) {
      this.$emit("select-report", report);
    },

    formatGroupLabel(group) {
      // Strip the "YYYY-MM - " prefix from the group label
      // Format in data: "2024-12 - Dezember 2024"
      // Display format: "Dezember 2024"
      if (!group) return group;
      const parts = group.split(" - ");
      return parts.length > 1 ? parts[1] : group;
    },

    formatTimeRange(duration) {
      if (!duration?.start?.timestamp) {
        return "Nicht festgelegt";
      }

      const startDate = duration.start.timestamp.toDate();
      const startFormatted = startDate.toLocaleString("de-DE", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      // Check if end timestamp exists
      if (duration.end?.timestamp) {
        const endDate = duration.end.timestamp.toDate();
        
        // Check if same day
        const sameDay = 
          startDate.getFullYear() === endDate.getFullYear() &&
          startDate.getMonth() === endDate.getMonth() &&
          startDate.getDate() === endDate.getDate();

        if (sameDay) {
          // Same day - only show end time
          const endTime = endDate.toLocaleString("de-DE", {
            hour: "2-digit",
            minute: "2-digit",
          });
          return `${startFormatted} - ${endTime}`;
        } else {
          // Different day - show full end date
          const endFormatted = endDate.toLocaleString("de-DE", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
          return `${startFormatted} - ${endFormatted}`;
        }
      } else if (duration.end?.estimatedTimestamp) {
        // Estimated end time
        const estimatedEnd = duration.end.estimatedTimestamp.toDate();
        const estimatedFormatted = estimatedEnd.toLocaleString("de-DE", {
          hour: "2-digit",
          minute: "2-digit",
        });
        return `${startFormatted} - ${estimatedFormatted} (erwartet)`;
      }

      // Only start time available
      return startFormatted;
    },

    timestampConverter(timestamp) {
      if (!timestamp) return "";
      const date = timestamp.toDate();
      return date.toLocaleString("de-DE", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    getSortLabel(sortBy, sortDesc) {
      if (!sortBy || sortBy.length === 0) return "Sortieren";
      const option = this.sortOptions.find(
        (opt) => opt.value === sortBy[0] && opt.desc === sortDesc[0]
      );
      return option ? option.text : "Sortieren";
    },

    updateSort(value, desc, tableEvents) {
      tableEvents["update:sort-by"]([value]);
      tableEvents["update:sort-desc"]([desc]);
    },

    getCountOfReportsInMonth(monthGroup) {
      return this.filteredReports.filter((report) => report.monthGroup === monthGroup)
        .length;
    },

    expandAllGroups() {
      // Click all closed groups
      Object.keys(this.$refs).forEach((key) => {
        const ref = this.$refs[key];
        if (ref && ref.$attrs && !ref.$attrs["data-open"]) {
          ref.$el.click();
        }
      });
      // Update states
      this.allGroupsExpanded = true;
      this.hasClosedGroups = false;
      this.hasOpenGroups = true;
    },

    collapseAllGroups() {
      // Click all open groups
      Object.keys(this.$refs).forEach((key) => {
        const ref = this.$refs[key];
        if (ref && ref.$attrs && ref.$attrs["data-open"]) {
          ref.$el.click();
        }
      });
      // Update states
      this.allGroupsExpanded = false;
      this.hasClosedGroups = true;
      this.hasOpenGroups = false;
    },

    onGroupToggle() {
      // Check current state and update flags - ONLY for visible groups
      this.$nextTick(() => {
        const refs = Object.keys(this.$refs);
        if (refs.length === 0) {
          // No visible groups - disable both buttons
          this.hasOpenGroups = false;
          this.hasClosedGroups = false;
          this.allGroupsExpanded = true;
          return;
        }

        const openGroups = [];
        const closedGroups = [];

        // Only check refs that actually exist (visible groups)
        refs.forEach((key) => {
          const ref = this.$refs[key];
          if (ref && ref.$attrs) {
            if (ref.$attrs["data-open"]) {
              openGroups.push(key);
            } else {
              closedGroups.push(key);
            }
          }
        });

        // Update state flags based ONLY on visible groups
        this.hasOpenGroups = openGroups.length > 0;
        this.hasClosedGroups = closedGroups.length > 0;
        this.allGroupsExpanded =
          closedGroups.length === 0 && openGroups.length > 0;
      });
    },

    updateGroupState() {
      // Trigger state check
      this.onGroupToggle();
    },

    resetGroupState() {
      // Reset to default state - useful when pagination/filters change
      this.$nextTick(() => {
        this.onGroupToggle();
      });
    },
  },
  watch: {
    filteredReports() {
      // Reset state when filtered data changes
      this.$nextTick(() => {
        this.resetGroupState();
      });
    },
  },
  mounted() {
    // Initialize state after component is mounted
    this.$nextTick(() => {
      this.resetGroupState();
    });
  },
  updated() {
    // Reset state when component updates
    this.$nextTick(() => {
      this.resetGroupState();
    });
  },
};
</script>

<style scoped>
.reports-list-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.reports-list-header {
  flex-shrink: 0;
}

.reports-list-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.compact-table >>> tbody tr {
  cursor: pointer;
}

.compact-table >>> .v-data-table__wrapper {
  overflow: visible;
}

.group-header {
  cursor: pointer;
  user-select: none;
}

.selected-item {
  background-color: rgba(25, 118, 210, 0.12) !important;
  border-left: 3px solid var(--v-primary-base, #1976d2);
}

.selected-item:hover {
  background-color: rgba(25, 118, 210, 0.16) !important;
}

/* Dark mode support */
.theme--dark .selected-item {
  background-color: rgba(25, 118, 210, 0.24) !important;
}

.theme--dark .selected-item:hover {
  background-color: rgba(25, 118, 210, 0.32) !important;
}
</style>
